import React, { useContext, useEffect, useState } from 'react';
import ScheduledItemsList from '../ScheduledItems/ScheduledItemsList';
import CancelledItemsList from '../CancelledItems/CancelledItemsList';
import getAllActiveItems from '../../service/all-items-service';
import getAllCancelledItems from '../../service/canceled-items-service';
import getAllFrequencies from '../../service/get-all-frequencies';
import { useApp } from '../../context/app-context';
import { ItemsContext } from '../ScheduledItems/ItemsContext';
import updateItemInfo from '../../service/update-item-info-service';
import { get, invoke } from 'lodash';
import formatString from '../../utils/formatString';
import appLabels from '../../utils/appLabels';
import { SessionContext, SessionContextType } from './SessioncContext';
import reactivateSubscription from '../../service/reactivate-subscription-service';

const SecondTabContent = () => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const { appData } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [activeItemsError, setActiveItemsError] = useState(false);
  const [cancelledItemsError, setCancelledItemsError] = useState(false);
  const [frequenciesError, setFrequenciesError] = useState(false);
  const [activeItems, setActiveItems] = useState([]);
  const [cancelledItems, setCancelledItems] = useState([]);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const { showToast } = useApp();
  const [frequencyOptions, setFrequencyOptions] = useState<object[]>([]);
  const [isAnalyticsDataReady, setIsAnalyticsDataReady] = useState(false);
  const [storeId, setStoreId] = useState<string>('');

  const getActiveItems = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const activeItemsList = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        getAllActiveItems(appData.environmentVariables, user),
      );
      setActiveItems(activeItemsList.data.itemsSubscribed || []);
      setCancellationReasons(activeItemsList.data.cancellationReasons || []);
    } catch (error) {
      setActiveItemsError(true);
    }
  };

  const getCancelledItems = async (): Promise<void> => {
    try {
      const cancelledItems = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        getAllCancelledItems(appData.environmentVariables, user),
      );
      setCancelledItems(get(cancelledItems, 'data.cancelledItems', []));
      setStoreId(get(cancelledItems, 'data.storeId', ''));

      setIsAnalyticsDataReady(true);
    } catch (error) {
      setCancelledItemsError(true);
    }
  };

  const getFrequencies = async (): Promise<void> => {
    try {
      const frequencies = await invoke(currentSessionContext, 'ensureActiveSession', () =>
        getAllFrequencies(appData.environmentVariables, user),
      );
      setFrequencyOptions(get(frequencies, 'data', []));
    } catch (error) {
      setFrequenciesError(true);
    }
    setIsLoading(false);
  };

  const refreshLists = async (): Promise<void> => {
    await getActiveItems();
    await getCancelledItems();
    await getFrequencies();
  };

  useEffect(() => {
    if (user && user.authToken) {
      refreshLists();
    }
  }, [user]);

  useEffect(() => {
    if (isAnalyticsDataReady) {
      analyticsEvents('scheduled-items', { activeItems, cancelledItems });
    }
  }, [isAnalyticsDataReady]);

  const analyticsEvents = (page, scheduledItems) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['AB']?.DATALAYER?.SNSInit(page, scheduledItems);
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  const ItemUpdateQuantityFn = async (props) => {
    const q = get(props, 'quantity');
    const maxQuantity = get(props, 'maxQuantity');
    const newQuantity = [1, maxQuantity, +q].sort((a, b) => a - b)[1];

    await invoke(currentSessionContext, 'ensureActiveSession', () =>
      updateItemInfo(appData.environmentVariables, user, {
        subscriptionId: get(props, 'subscriptionId'),
        quantity: newQuantity,
        itemBpn: get(props, 'itemBpn'),
        isReplinishItem: get(props, 'isReplinishItem', true),
      }),
    );

    showToast(
      'info',
      formatString(appLabels.QuantitySelector.updatedText, {
        itemDescription: get(props, 'item.itemDescription'),
      }),
    );
  };

  const rescheduleItem = async (itemInfo) => {
    return reactivateSubscription(appData.environmentVariables, user, itemInfo);
  };

  return (
    <>
      <ItemsContext.Provider
        value={{
          updateQuantityFn: ItemUpdateQuantityFn,
          fetchItemsInfo: refreshLists,
          storeId: storeId,
          frequencyOptions: frequencyOptions,
        }}
      >
        <ScheduledItemsList
          loading={isLoading}
          items={activeItems}
          refreshHandler={refreshLists}
          apiError={activeItemsError}
          cancellationReasons={cancellationReasons}
          showSubstitutionButton={true}
          showEditSubstitution={'ALL_ITEMS'} //TODO move to enum later
        />
      </ItemsContext.Provider>

      <ItemsContext.Provider
        value={{
          updateQuantityFn: async () => {},
          fetchItemsInfo: refreshLists,
          rescheduleItem,
          storeId: storeId,
          frequencyOptions: frequencyOptions,
        }}
      >
        <CancelledItemsList
          loading={isLoading}
          items={cancelledItems}
          refreshHandler={refreshLists}
          apiError={cancelledItemsError}
        />
      </ItemsContext.Provider>
    </>
  );
};

export default SecondTabContent;
